import { useEffect, useState } from "react";

import { ISku } from "@/interfaces/sku.interface";

import {
  CampaignCouponError,
  CampaignCouponLookup,
  resolveCampaignCouponForSku,
} from "../../form-data";

const DEBOUNCE_DELAY = 400;

const emptyLookup: CampaignCouponLookup = {
  campaign: null,
  unitPrice: null,
  error: null,
  minimumQuantity: null,
};

type UseCampaignCouponPriceProps = {
  sku: ISku | null;
  quantity: number;
  campaignId?: number | null;
  couponCode: string;
  contactId?: number | null;
  skuUnitPrice: number;
};

const useCampaignCouponPrice = ({
  sku,
  quantity,
  campaignId,
  couponCode,
  contactId,
  skuUnitPrice,
}: UseCampaignCouponPriceProps) => {
  const [debouncedCode, setDebouncedCode] = useState(couponCode.trim());
  const [isLoading, setIsLoading] = useState(false);
  const [result, setResult] = useState<CampaignCouponLookup>(emptyLookup);

  useEffect(() => {
    const trimmed = couponCode.trim();
    setResult(emptyLookup);
    if (!trimmed && !campaignId) {
      setDebouncedCode("");
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const timer = setTimeout(() => setDebouncedCode(trimmed), DEBOUNCE_DELAY);
    return () => clearTimeout(timer);
  }, [couponCode, campaignId]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (!sku || (!debouncedCode && !campaignId)) {
        setResult(emptyLookup);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const lookup = await resolveCampaignCouponForSku({
          sku,
          quantity,
          campaignId,
          couponCode: debouncedCode,
          contactId,
        });
        if (!cancelled) setResult(lookup);
      } catch {
        if (!cancelled) setResult({ ...emptyLookup, error: debouncedCode ? "invalid" : null });
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [campaignId, contactId, debouncedCode, quantity, sku]);

  return {
    unitPrice: result.unitPrice ?? skuUnitPrice,
    isLoading,
    error: result.error as CampaignCouponError | null,
    campaignId: result.campaign?.id ?? null,
    campaignNetUnitPrice: result.unitPrice,
    minimumQuantity: result.minimumQuantity,
  };
};

export default useCampaignCouponPrice;
